/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import i18n from 'i18n';
import {
  Button as MaterialButton,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Add as AddIcon, Refresh as RefreshIcon } from '@material-ui/icons';
import Button from 'components/Button';
import Permission from 'components/ValidPermission';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import ApplicationState from 'store/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import { useTableFilters } from 'hooks/useTableFilters';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import {
  EnvironmentFields,
  InstanceStateEnum,
  ListServerFieldsDataProps,
  ServerFields,
} from 'modules/environments/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import {
  attachServerRequest,
  detachServerRequest,
  getAllServersRequest,
  restartServerRequest,
  enableServerMonitoringManagementRequest,
  disableServerMonitoringManagementRequest,
} from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { cyrb53Hash } from 'utils/helpers';
import {
  AttachServerRequestProps,
  DetachServerRequestProps,
  GetAllServersRequestProps,
} from 'modules/environments/requestInterfaces';
import { Alert } from '@material-ui/lab';
import styles from '../styles';
import { useServerActions } from '../useServerActions';
import FormSupportServer from './FormSupportServer';
import StatusInstanceServer, {
  getStatusText,
  isFinalizingState,
} from '../statusServer';
import ServerMonitoringStatus from './ServerMonitoringStatus';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'can_connect_instance',
  'detach_support_server',
  'start_support_server',
  'stop_support_server',
];

interface MenuActionItemProps {
  key: string | number;
  id?: string;
  label: string;
  onClick: () => void;
  hidden?: boolean;
  style?: React.CSSProperties;
}

interface SupportServersProps {
  companySelected: CompanyData | null;
  environmentSelected: EnvironmentFields | null;
  isLoading: boolean;
  permissions?: PermissionsData;
  role?: RolesEnum;
  supportServers: ListServerFieldsDataProps;
  attachServerRequest: (props: AttachServerRequestProps) => void;
  disableServerMonitoringManagementRequest: ({
    serverId,
    environmentUuid,
    type,
    query,
  }) => void;
  detachServerRequest: (props: DetachServerRequestProps) => void;
  getAllServersRequest: (props: GetAllServersRequestProps) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  restartServerRequest: ({ id, environmentUuid, query }) => void;
  enableServerMonitoringManagementRequest: ({
    serverId,
    environmentUuid,
    type,
    query,
  }) => void;
}

const SupportServers = ({
  companySelected,
  environmentSelected,
  isLoading,
  permissions,
  role,
  supportServers,
  attachServerRequest,
  disableServerMonitoringManagementRequest,
  detachServerRequest,
  getAllServersRequest,
  modalClose,
  modalOpen,
  restartServerRequest,
  enableServerMonitoringManagementRequest,
}: SupportServersProps) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const { query, handleLimitChange, handlePageChange } = useTableFilters();
  const {
    handleConnect,
    handleStartServer,
    handleStopServer,
  } = useServerActions(query);

  const [refresh, setRefresh] = React.useState(true);
  const [alertMessage, setAlertMessage] = React.useState(false);
  const [stopRequest, setStopRequest] = React.useState(true);

  const intervalRef = React.useRef<any>();

  const MONITORING_RELOAD_TIME = 10000;

  const isAllFinalizing = React.useMemo(
    () =>
      !!supportServers?.results?.every(
        s =>
          isFinalizingState(s?.instance_state) &&
          isFinalizingState(s?.image_publication_status)
      ),
    [supportServers]
  );

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: 'instance_id',
      i18n: 'ID',
      hidden: isClientOperator(role),
    },
    {
      key: 'name',
      i18n: 'NAME_KEY',
    },
    {
      key: 'public_ip',
      i18n: 'PUBLIC_IP_KEY',
    },
    {
      key: 'public_ip',
      i18n: 'PRIVATE_IP_KEY',
    },
    {
      key: 'flavor',
      i18n: 'FLAVOR_KEY',
      centerTitle: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'instance_state',
      i18n: 'STATUS_KEY',
    },
    {
      key: 'server_monitoring_status',
      i18n: 'MONITORING_KEY',
      order: false,
      centerTitle: true,
      hidden: !permissions?.can_view_server_monitoring_status,
    },
    {
      key: 'server_type',
      i18n: 'TYPE_KEY',
      order: false,
      centerTitle: true,
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions &&
        requiredActionsPermissions.every(
          permission => !permissions[permission]
        ),
    },
  ].filter(header => !header.hidden);

  const getSupportServers = (refresh = false, getFromProvider = false) => {
    getAllServersRequest({
      id,
      query,
      force_refresh: getFromProvider,
      type: 'support',
    });
    setRefresh(refresh);
  };

  const hasAnyStoppingMonitoring = React.useMemo(
    () =>
      permissions?.can_view_server_monitoring_status &&
      !!supportServers?.results?.some(s => !!s?.is_stopping_monitoring),
    [supportServers, permissions]
  );

  React.useEffect(() => {
    setStopRequest(!hasAnyStoppingMonitoring && isAllFinalizing);
  }, [hasAnyStoppingMonitoring, isAllFinalizing]);

  React.useEffect(() => {
    if (stopRequest) {
      clearInterval(intervalRef.current);
      setRefresh(false);
    } else {
      intervalRef.current = setInterval(() => {
        getSupportServers(true);
      }, MONITORING_RELOAD_TIME);
    }

    return () => clearInterval(intervalRef.current);
  }, [stopRequest]);

  React.useEffect(() => {
    getSupportServers();
  }, [id, query]);

  React.useEffect(() => {
    const hasApplicationServer = supportServers?.results?.some(
      item => !!item?.is_application_server
    );
    setAlertMessage(
      !hasApplicationServer && environmentSelected?.behavior?.type === 'micro'
    );
  }, [supportServers, isLoading, environmentSelected, id]);

  const handleAttachSupportServer = () =>
    modalOpen({
      title: i18n.t('ADD_SERVER_SUPPORT_KEY'),
      subtitle: i18n.t('ADD_SERVER_SUPPORT_SUBTITLE_KEY', {
        environment: environmentSelected?.name,
      }),
      content: (
        <FormSupportServer
          environment={environmentSelected}
          isLoading={isLoading}
          onCancel={modalClose}
          onSubmit={values => {
            const valuesToSubmit = { ...values };

            if (!permissions?.can_enable_server_monitoring_management) {
              valuesToSubmit.monitoring_management_enabled = false;
            }

            attachServerRequest({
              environmentUuid: id,
              values: valuesToSubmit,
              type: 'support',
              query,
            });
            modalClose();
          }}
        />
      ),
    });

  const handleTransformInServerApplication = (item: ServerFields) =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.TURN_INTO_APPLICATION_SERVER_KEY'),
      content: i18n.t(
        'ENVIRONMENTS_PAGE.TURN_INTO_APPLICATION_SERVER_MSG_KEY',
        {
          instanceId: item?.instance_id,
        }
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              attachServerRequest({
                environmentUuid: id,
                type: 'support',
                values: {
                  instance_id: item?.instance_id,
                  include_instance_as_application_server: true,
                  monitoring_management_enabled:
                    item?.monitoring_management_enabled,
                },
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleDetachServerSupport = (instanceId: string, name: string) =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_MSG_KEY', {
        environment: name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              detachServerRequest({
                environmentUuid: id,
                instanceId,
                query,
                type: 'support',
              });
              modalClose();
            }}
          >
            {i18n.t('UNLINK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleRestartServer = (item: ServerFields) =>
    modalOpen({
      title: i18n.t('WANT_REBOOT_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.RESTART_SERVER_MSG_KEY', {
        environment: item?.name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              restartServerRequest({
                id: item?.instance_id,
                environmentUuid: id,
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('REINITIALIZE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleEnableServerMonitoringManagement = item =>
    modalOpen({
      title: i18n.t('ENABLE_SERVER_MONITORING_MANAGEMENT_MODAL_TITLE_KEY'),
      content: i18n.t(
        'ENABLE_SERVER_MONITORING_MANAGEMENT_MODAL_DESCRIPTION_KEY'
      ),
      footer: (
        <Grid>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              enableServerMonitoringManagementRequest({
                serverId: item?.instance_id,
                environmentUuid: id,
                type: 'support',
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleDisableServerMonitoringManagement = item =>
    modalOpen({
      title: i18n.t('DISABLE_SERVER_MONITORING_MANAGEMENT_MODAL_TITLE_KEY'),
      content: i18n.t(
        'DISABLE_SERVER_MONITORING_MANAGEMENT_MODAL_DESCRIPTION_KEY'
      ),
      footer: (
        <Grid>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              disableServerMonitoringManagementRequest({
                serverId: item?.instance_id,
                environmentUuid: id,
                type: 'support',
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
        </Grid>
      ),
    });

  const isApplicationServer = (item: ServerFields) =>
    item?.is_application_server &&
    item?.server_type === 'support' &&
    environmentSelected?.behavior?.type === 'micro';

  const canTransformServerIntoApplicationServer = (
    isApplicationServer: boolean
  ) => !isApplicationServer && environmentSelected?.behavior.type === 'micro';

  const getMenuActionItems = (item: ServerFields): MenuActionItemProps[] => {
    const menuActionItems: MenuActionItemProps[] = [
      {
        key: 'btn-transform-server-aplication',
        label: i18n.t('ENVIRONMENTS_PAGE.TURN_INTO_APPLICATION_SERVER_KEY'),
        onClick: () => handleTransformInServerApplication(item),
        hidden:
          !canTransformServerIntoApplicationServer(
            item?.is_application_server
          ) || !permissions?.attach_support_server,
      },
      {
        key: 'btn-unlink-server',
        label: i18n.t('UNLINK_KEY'),
        onClick: () => handleDetachServerSupport(item?.instance_id, item?.name),
        hidden: !permissions?.detach_support_server,
      },
      {
        key: 'btn-start-support-server',
        label: i18n.t('ENVIRONMENTS_PAGE.START_SERVER_KEY'),
        onClick: () => handleStartServer(item),
        hidden:
          item?.instance_state !== InstanceStateEnum.STOPPED ||
          !permissions?.start_support_server,
      },
      {
        key: 'btn-stop-support-server',
        label: i18n.t('ENVIRONMENTS_PAGE.STOP_SERVER_KEY'),
        onClick: () => handleStopServer(item),
        hidden:
          item?.is_stopping_monitoring ||
          item?.instance_state !== InstanceStateEnum.RUNNING ||
          !permissions?.stop_support_server,
      },
      {
        key: 'btn-reboot-support-server',
        label: i18n.t('REINITIALIZE_KEY'),
        onClick: () => handleRestartServer(item),
        hidden:
          item?.is_stopping_monitoring ||
          !permissions?.stop_support_server ||
          item?.instance_state !== InstanceStateEnum.RUNNING,
      },
      {
        key: 'btn-enable-support-server-monitoring-management',
        label: i18n.t('CAN_ENABLE_SERVER_MONITORING_MANAGEMENT'),
        onClick: () => handleEnableServerMonitoringManagement(item),
        hidden:
          !permissions?.can_enable_server_monitoring_management ||
          !!item?.monitoring_management_enabled,
      },
      {
        key: 'btn-disable-support-server-monitoring-management',
        label: i18n.t('CAN_DISABLE_SERVER_MONITORING_MANAGEMENT'),
        onClick: () => handleDisableServerMonitoringManagement(item),
        hidden:
          !permissions?.can_disable_server_monitoring_management ||
          !item?.monitoring_management_enabled,
      },
    ];

    const connectAsItems = item?.connection_usernames?.map(
      (username, index) => ({
        key: cyrb53Hash(username),
        id: `btn-connect-as-support-${index}`,
        label: `${i18n.t('CONNECT_LIKE_KEY')} ${username}`,
        onClick: () => handleConnect(item?.instance_id, username),
        hidden: !permissions?.can_connect_instance,
        style: {
          borderTop: index === 0 ? '1px solid #EEE' : '',
        },
      })
    );

    if (connectAsItems) {
      menuActionItems.push(...connectAsItems);
    }

    return menuActionItems.filter(action => !action.hidden);
  };

  return (
    <>
      <Divider className={classes.divider} />
      {!!alertMessage && (
        <Alert severity="warning" color="error">
          <span>{i18n.t('ENVIRONMENTS_PAGE.ERROR_CONFIG_SERVER_KEY')}</span>
        </Alert>
      )}

      <Grid container justify="space-between" alignItems="center">
        <Typography variant="h6" component="h2">
          {i18n.t('SUPPORT_SERVERS_KEY')}
        </Typography>
        <Grid item className={classes.serverActionsContainer}>
          <Permission value="attach_support_server">
            <MaterialButton
              id="btn-add-server-support"
              className={classes.addServerButton}
              startIcon={<AddIcon />}
              onClick={() => handleAttachSupportServer()}
            >
              {i18n.t('ADD_KEY')}
            </MaterialButton>
          </Permission>

          <Button
            id="btn-sync-with-provider-support"
            onClick={() => getSupportServers(false, true)}
            variant="outlined"
            startIcon={<RefreshIcon />}
            className={clsx({
              [classes.superAdminSyncButton]: inSuperAdminView(
                role,
                companySelected
              ),
              [classes.adminSyncButton]: inAdminView(role, companySelected),
              [classes.operatorSyncButton]: inOperatorView(
                role,
                companySelected
              ),
              [classes.syncButton]: companySelected,
            })}
          >
            {i18n.t('ENVIRONMENTS_PAGE.SYNC_WITH_PROVIDER')}
          </Button>
        </Grid>
      </Grid>

      <TableItems
        header={headers.map(header => (
          <TableCell key={header.key} centerTitle={header.centerTitle}>
            {i18n.t(header.i18n)}
          </TableCell>
        ))}
        renderItems={(item: ServerFields, index) =>
          item?.server_type === 'support' && (
            <TableRow key={item?.instance_id}>
              {!isClientOperator(role) && (
                <TableCell ellipsis copyable maxLength={20}>
                  {item?.instance_id}
                </TableCell>
              )}
              <TableCell>{item?.name}</TableCell>
              <TableCell copyable tooltip={item?.public_ip}>
                {item?.public_ip}
              </TableCell>
              <TableCell copyable tooltip={item?.private_ip}>
                {item?.private_ip}
              </TableCell>
              {!isClientOperator(role) && (
                <TableCell>
                  <span className={classes.flag}>{item?.flavor}</span>
                </TableCell>
              )}
              <TableCell
                align="center"
                tooltip={getStatusText(item?.instance_state)}
              >
                <StatusInstanceServer value={item?.instance_state} />
              </TableCell>
              <Permission value="can_view_server_monitoring_status">
                <ServerMonitoringStatus server={item} />
              </Permission>

              <TableCell>
                <span
                  className={clsx(classes.flag, {
                    [classes.flagPink]: item?.server_type === 'support',
                  })}
                >
                  {isApplicationServer(item)
                    ? i18n.t('ENVIRONMENTS_PAGE.APPLICATION_KEY')
                    : i18n.t('SUPPORT')}
                </span>
              </TableCell>
              <Permission value={requiredActionsPermissions}>
                <TableCell textAlignCenter>
                  <MenuActions id={`btn-options-srv-support-${index}`}>
                    {(() => {
                      const items = getMenuActionItems(item);
                      return items.length > 0 ? (
                        items.map(action => (
                          <MenuActionItem
                            key={action?.key}
                            id={action?.id ?? action?.key}
                            onClick={action?.onClick}
                            style={action?.style}
                          >
                            {action?.label}
                          </MenuActionItem>
                        ))
                      ) : (
                        <MenuActionItem>
                          {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                        </MenuActionItem>
                      );
                    })()}
                  </MenuActions>
                </TableCell>
              </Permission>
            </TableRow>
          )
        }
        data={supportServers}
        isLoading={!refresh && isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_SUPPORT_SERVER_KEY')}
      />
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  environments: {
    environmentSelected,
    supportServers,
    isLoadingSupportServers,
  },
}: ApplicationState) => ({
  companySelected,
  environmentSelected,
  isLoading: isLoadingSupportServers,
  permissions: data?.permissions,
  role: data?.role,
  supportServers,
});

export default connect(mapStateToProps, {
  attachServerRequest,
  disableServerMonitoringManagementRequest,
  detachServerRequest,
  enableServerMonitoringManagementRequest,
  getAllServersRequest,
  modalClose,
  modalOpen,
  restartServerRequest,
})(SupportServers);
